import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import MainSearchBar from "../../../components/MainSearchbar";
import CustomButton from "../../../components/CustomButton";
import FIltersDropdown from "../../../components/Filters/FiltersDropdown";
import Pagination from "../../../components/CustomPagination";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import UserDetailsDrawer from "../../../components/Drawers/UserDetailsDrawer";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import { useNavigate } from "react-router-dom";
import {
  useGetAllBackendUserList,
  useGetBackendUserDetails,
} from "../../../custom-hooks/queries";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import DropdownMenu from "../../../components/Dropdown";
import UserActionDropdown from "../../../components/UserActionDropdown";
import { useScreenWidth } from "../../../custom-hooks/useScreenWidth";
import { SearchNotFound } from "../../../components/SearchNotFound";
import CustomTable from "../../../components/CustomTable";
import { PageHeader } from "../../../components/PageHeader";
import { sortConfigToBackendUsersApiParams } from "../../../utils/backendUsersGridSort";
import { sortBackendUserItems } from "../../../utils/adminGridClientSort";
import { useAdminSortState } from "../../../custom-hooks/useAdminSortState";

const BackendUsersList = () => {
  const { sortConfig, currentPage, setCurrentPage, handleSort } = useAdminSortState();
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["adminUsers"]
    ) || "";

  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "adminUsers",
          searchText: search,
        })
      );
      setCurrentPage(1);
    }, 300),
    [dispatch]
  );

  const screenWidth = useScreenWidth();

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };

  const serverSortParams = sortConfigToBackendUsersApiParams(sortConfig);

  const { data: backendUsers, isLoading } = useGetAllBackendUserList(
    currentPage,
    searchQuery,
    serverSortParams
  );
  const { data: userDetails } = useGetBackendUserDetails(expandedRow);

  const handleExpandRow = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      dispatch(setDrawer(false));
    } else {
      setExpandedRow(id);
      dispatch(setDrawer(true));
    }
  };

  const formatUserDetails = (details) => {
    if (!details) return null;

    return {
      id: details.id,
      name: details.full_name,
      email: details.email,
      phone: details.phone_number,
      officePhone: details.office_phone_number,
      firstName: details.first_name,
      lastName: details.last_name,
      createdDate: details.create_date_time,
      updatedDate: details.update_date_time,
      status: details.is_active ? "Active" : "Inactive",
      role: details.role,
      contactPreference: details.contact_preference,
    };
  };

  const handleAddNewAccount = () => {
    navigate("/Backend/Users/AddBackendUser");
  };

  const handleManageUser = (userId) => {
    navigate(`/Backend/Users/BackendUsers/${userId}/manage`);
  };

  const getStatusBadge = (isActive) => {
    return (
      <span
        className={`inline-flex items-center rounded-md ${
          isActive
            ? "bg-green-50 text-green-700 ring-green-600/20"
            : "bg-red-50 text-red-700 ring-red-600/10"
        } px-2 py-1 text-xs font-medium ring-1 ring-inset`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const sortedItems = sortBackendUserItems(backendUsers?.items, sortConfig, serverSortParams);

  // Define columns for CustomTable
  const columns = [
    {
      key: "first_name",
      header: "First Name",
      sortable: true,
      width: "16.67%" /* w-1/6 */,
      render: (item) => (
        <span
          className="hover:text-gray-700 hover:cursor-pointer hover:underline"
          onClick={() => handleExpandRow(item?.id)}
        >
          {item.first_name}
        </span>
      ),
    },
    {
      key: "last_name",
      header: "Last Name",
      sortable: true,
      width: "16.67%" /* w-1/6 */,
    },
    {
      key: "email",
      header: "Email Address",
      sortable: true,
      width: "16.67%" /* w-1/6 */,
    },
    {
      key: "is_active",
      header: "Status",
      sortable: true,
      width: "16.67%" /* w-1/6 */,
      align: "center",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (item) => getStatusBadge(item.is_active),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      align: "right",
      headerClassName: "text-end",
      cellClassName: "text-right",
      render: (item) => (
        <UserActionDropdown
          onViewDetails={() => handleExpandRow(item.id)}
          onManageUser={() => handleManageUser(item.id)}
        />
      ),
    },
  ];

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gray-50">
      <PageHeader heading={"Admins"} />

      <div className="mt-8">
        {isLoading ? (
          <SkeletonLoader />
        ) : backendUsers?.items?.length === 0 ? (
          <SearchNotFound
            desc={" There are currently no users to display."}
            title={"No users found"}
          />
        ) : (
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-1">
              <div className="mt-8 mx-5 bg-white shadow-md rounded-md py-6 px-2 border border-gray-300">
                <div className="flex justify-between items-center mb-5 px-5">
                  <div>
                    <MainSearchBar
                      pageKey="adminUsers"
                      placeholder="Search Users..."
                      height="40px"
                      width={
                        screenWidth <= 375
                          ? "17.5rem"
                          : screenWidth < 425
                          ? "22rem"
                          : "25rem"
                      }
                      marginRight={"mr-2"}
                      isSearchIcon={true}
                      onSearch={onSubmit}
                    />
                  </div>

                  <div>
                    <CustomButton
                      color="bg-indigo-600"
                      size="text-base"
                      weight="font-normal"
                      height="h-9"
                      width="w-32"
                      textColor="text-white"
                      text="Add User"
                      borderRadius="0.375rem"
                      onClick={handleAddNewAccount}
                    />
                  </div>
                </div>{" "}
                <CustomTable
                  columns={columns}
                  data={sortedItems}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  emptyMessage="No users found"
                  emptyDescription="There are currently no users to display."
                  isSearchActive={!!searchQuery}
                  className="admin-users-table"
                  edgeToEdge
                  onRowClick={(item) => handleManageUser(item?.id)}
                />
                {!isLoading && backendUsers?.items?.length > 0 && (
                  <div className="mt-5">
                    <Pagination
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                      totalPages={backendUsers?.total_pages || 1}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {expandedRow && userDetails && (
          <UserDetailsDrawer
            details={formatUserDetails(userDetails)}
            onClose={() => {
              setExpandedRow(null);
              dispatch(setDrawer(false));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BackendUsersList;
